import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { LayoutDashboard, LogOut, Shield, User } from "lucide-react";
import { Logo } from "@/components/Logo";

export const Navbar = () => {
  const { t } = useI18n();
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-40 glass border-b border-black/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" data-testid="logo-link" className="flex items-center shrink-0 hover:opacity-90 transition-opacity">
          <Logo />
        </Link>

        <div className="flex items-center gap-1 sm:gap-2">
          <Link to="/activities" data-testid="nav-activities" className="hidden sm:block rounded-full px-4 py-2 text-sm font-medium hover:bg-secondary transition-colors">
            {t("nav_activities")}
          </Link>
          <LanguageSwitcher />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu" className="ml-1">
                  <Avatar className="h-9 w-9 border border-black/10">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback>{(user.name || "U")[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-semibold truncate">{user.name}</p>
                  <p className="text-muted-foreground truncate text-xs">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard className="h-4 w-4 mr-2" /> {t("nav_dashboard")}
                </DropdownMenuItem>
                {(user.role === "admin" || user.role === "vendor") && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield className="h-4 w-4 mr-2" /> {user.role === "admin" ? t("nav_admin") : "My Listings"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut className="h-4 w-4 mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="login-btn" onClick={login} className="rounded-full gap-2">
              <User className="h-4 w-4" /> {t("login")}
            </Button>
          )}
        </div>
      </nav>
    </header>
  );
};
